import { useMutation, useQuery } from '@tanstack/react-query';
import fetcher, {
	FleetPaginated,
	FleetPagination,
	FleetQueryParams,
} from '../fetcher';

// --- MKL Search SPF ---

export type SearchMklSpf = {
	spfId: number;
	spfNo?: string;
	customerId?: string;
	customerName?: string;
	groupBmppId?: string;
	groupBmppName?: string;
	expiredDate?: string;
	status?: string;
};

export type SearchMKLParam = FleetQueryParams & {
	pagination: FleetPagination;
};

export const searchMklSpf = (params: SearchMKLParam) => {
	return fetcher<FleetPaginated<SearchMklSpf>>({
		api: '/mkl/search-spf',
		body: params,
	});
};

export const useMKLSearch = (param: SearchMKLParam) => {
	return useQuery({
		queryKey: ['searchMklSpf', param] as const,
		queryFn: () => searchMklSpf(param),
	});
};

// --- MKL Get Detail ---

export type MklFinancingLimit = {
	mklNo?: string;
	financingLimitAmount?: number;
	financingLimitNature?: string;
	financingLimitType?: string;
	plafonType?: string;
	expiredDate?: string;
	ntfAmount?: number;
	outstandingPrincipalAmount?: number;
	outstandingLimitAmount?: number;
};

export type SpfApprovalHistory = {
	id: number;
	name?: string;
	date?: string;
	process?: string;
	status?: string;
	notes?: string;
	spfNo?: string;
	spfId?: number;
};

export type MklFileResponse = {
	id?: string;
	name?: string;
	url?: string;
	path?: string;
	format?: string;
};

export type MklDetail = {
	spfId?: number;
	spfNo?: string;
	prospectId?: string;
	napResolutionId?: number;
	customerId?: string;
	customerName?: string;
	groupBmppId?: string;
	groupBmppName?: string;
	resolutionFile?: MklFileResponse;
	financingLimits?: MklFinancingLimit[];
	spfApprovalHistory?: SpfApprovalHistory[];
};

export const getMklDetail = (spfId: number) => {
	return fetcher<MklDetail>({
		api: '/mkl/get-detail',
		body: { spfId },
	});
};

export const useGetMKLDetail = (param: { mklId: number }) => {
	return useQuery({
		queryKey: ['getMklDetail', param] as const,
		queryFn: () => getMklDetail(param.mklId),
	});
};

// Keep old upload definition if SPF list still imports it
export type UploadSPFWithMKLPayload = {
	spfId: number;
	document: {
		id: string;
		name: string;
		path: string;
	};
};

export type UploadSPFWithMKLResponse = {
	success: boolean;
	mklNumbers: {
		noMKL: string;
		limitPembiayaan: number;
		sifatLimit: string;
		jenisLimit: string;
		expiredDate: string;
	}[];
};

export const uploadSPFWithMKL = (body: UploadSPFWithMKLPayload) => {
	return fetcher<UploadSPFWithMKLResponse>({
		api: '/spf/upload-signed-document',
		body,
	});
};

export const useUploadSPFWithMKL = () => {
	return useMutation({
		mutationFn: uploadSPFWithMKL,
	});
};
